import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";
import { Package } from "lucide-react";

export default function LoginPage() {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("欢迎回来");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left brand panel - deep bronze */}
            <div className="hidden lg:flex relative text-white p-12 flex-col justify-between grain" style={{ background: "linear-gradient(135deg, #2A1F10 0%, #3A2A15 45%, #4A3620 100%)" }}>
                {/* Gold accent glow */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 30% 20%, rgba(176,138,62,0.25) 0%, transparent 55%)" }} />
                <div className="flex items-center gap-3 relative z-10">
                    <div className="h-10 w-10 rounded-md bg-gold-500 flex items-center justify-center shadow-lg shadow-gold-900/40">
                        <Package className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="text-sm tracking-wider uppercase" style={{ color: "#D9C591" }}>FuYuan Jewelry</div>
                        <div className="text-lg font-semibold">福缘库存管理</div>
                    </div>
                </div>

                <div className="relative z-10 space-y-6 max-w-lg">
                    <h1 className="text-5xl font-black leading-tight tracking-tight">
                        缘起福生<br />
                        <span className="text-gold-400">智启未来</span>
                    </h1>
                    <p className="text-base leading-relaxed" style={{ color: "#E7D9B6" }}>
                        每一件饰品都是一份福缘。精准管理库存状态，让好运与业绩共同增长。
                    </p>
                </div>

                <div className="text-xs relative z-10" style={{ color: "#8A6B2E" }}>© 2026 FuYuan Jewelry · All Rights Reserved</div>
            </div>

            {/* Right form panel - cream */}
            <div className="flex items-center justify-center p-8" style={{ background: "linear-gradient(180deg, #FBF7EF 0%, #F5EBD1 100%)" }}>
                <div className="w-full max-w-sm bg-white/70 backdrop-blur-sm rounded-xl border border-gold-200 shadow-xl shadow-gold-900/5 p-8">
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <div className="h-9 w-9 rounded-md bg-gold-600 flex items-center justify-center">
                            <Package className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-lg font-semibold">福缘库存管理</span>
                    </div>

                    <h2 className="text-3xl font-bold tracking-tight text-slate-900">登录账户</h2>
                    <p className="text-sm text-gold-700/70 mt-2">输入邮箱与密码开始管理你的库存</p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-gold-500 font-semibold">邮箱</Label>
                            <Input
                                id="email"
                                data-testid="login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="h-11"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-gold-500 font-semibold">密码</Label>
                            <Input
                                id="password"
                                data-testid="login-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                className="h-11"
                            />
                        </div>

                        <Button
                            type="submit"
                            data-testid="login-submit-btn"
                            disabled={loading}
                            className="w-full h-11 bg-gold-600 hover:bg-gold-700 text-white font-semibold transition-colors duration-150"
                        >
                            {loading ? "处理中..." : "登录"}
                        </Button>

                        <p className="text-xs text-slate-400 text-center pt-4 leading-relaxed">
                            如需添加更多员工账户，请先用主账户登录，<br />
                            然后前往「用户管理」页面。
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}
